/**
 * Announcement bar rotation (`sections/announcement-bar.liquid`).
 *
 * The section renders every announcement and hides all but the first, which is the right
 * no-JS result: one message, legible, no dependency on this file. What was missing is the
 * part that makes more than one message worth adding — nothing ever advanced them, so a
 * merchant who wrote three saw the first for ever.
 *
 * Auto-advance stops under `prefers-reduced-motion`, and while the pointer or keyboard
 * focus is inside the bar. A message can carry a link, and a link that slides away
 * mid-click is worse than one that waits.
 *
 * The prev/next fader buttons (reference: `.fader.left`/`.fader.right`) are wired up
 * whichever way that setting comes out: manually stepping to a message on request is not
 * the auto-playing motion the setting exists to stop, so it stays live even when the
 * interval below never starts — a reduced-motion visitor still gets a working pair of
 * buttons, just no message that moves on its own.
 */
(function () {
  'use strict';

  /* Long enough to read a short sentence twice over. */
  const INTERVAL = 5000;

  document.querySelectorAll('[data-announcement-bar]').forEach((bar) => {
    const items = Array.from(bar.querySelectorAll('.announcement-bar__item'));
    if (items.length < 2) return;

    const prevButton = bar.querySelector('[data-announcement-prev]');
    const nextButton = bar.querySelector('[data-announcement-next]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* Taken over from Liquid's `hidden`, which is display-based and cannot cross-fade.
       The attribute is what tells the stylesheet to switch to the stacked, faded layout,
       so the two never disagree about which mechanism is hiding a message. Done
       regardless of reduced motion — the buttons need this layout to have anything to
       step between — only the automatic timer below is what reduced motion cancels. */
    items.forEach((item) => item.removeAttribute('hidden'));
    bar.setAttribute('data-rotating', '');
    items[0].classList.add('is-active');

    let index = 0;
    let paused = false;

    function show(newIndex) {
      items[index].classList.remove('is-active');
      index = (newIndex + items.length) % items.length;
      items[index].classList.add('is-active');
    }

    if (!reducedMotion) {
      setInterval(() => {
        if (paused || document.hidden) return;
        show(index + 1);
      }, INTERVAL);

      bar.addEventListener('pointerenter', () => {
        paused = true;
      });
      bar.addEventListener('pointerleave', () => {
        paused = false;
      });
      bar.addEventListener('focusin', () => {
        paused = true;
      });
      bar.addEventListener('focusout', () => {
        paused = false;
      });
    }

    /* A manual step also counts as the visitor having engaged with the bar — pausing
       here keeps a deliberate click from being immediately undone by the timer landing
       moments later on whatever message happened to be next. */
    if (prevButton) {
      prevButton.addEventListener('click', () => {
        paused = true;
        show(index - 1);
      });
    }
    if (nextButton) {
      nextButton.addEventListener('click', () => {
        paused = true;
        show(index + 1);
      });
    }
  });
})();
